import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  ArrowDown2,
  Arrowleft3,
  CalendarSearch,
  OutlineArrowRight4,
} from "../../Icon";
import { alpha, useTheme } from "@mui/material";
import "dayjs/locale/es";
import "dayjs/locale/en";
import "./EGDatePicker.scss";

const IconDatepicker = () => {
  const theme = useTheme();
  return <CalendarSearch color={theme.palette.blueGray200.main} size={24} />;
};

const LeftArrowIcon = () => {
  const theme = useTheme();
  return <Arrowleft3 color={theme.palette.secondary.dark} size={10} />;
};

const RightArrowIcon = () => {
  const theme = useTheme();
  return <OutlineArrowRight4 color={theme.palette.secondary.dark} size={10} />;
};

const SwitchViewIcon = () => {
  const theme = useTheme();
  return <ArrowDown2 color={theme.palette.secondary.dark} size={10} />;
};

const EGDatePicker = ({
  label = "Select Date",
  value,
  maxDate,
  minDate,
  onChange,
  isValid,
  showSuccessValidation,
  helperText = "",
  inputRef,
  onBlur,
  format = "DD/MMM/YYYY",
  required = false,
  localeText,
  ...rest
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const adapterLocale = (i18n?.language || "es")
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "es";

  dayjs.extend(updateLocale);
  dayjs.updateLocale("en", {
    weekStart: 0,
    months: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
  });
  dayjs.updateLocale("es", {
    weekStart: 0,
    months: [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ],
  });

  // Función auxiliar para obtener el color del borde según el estado
  const getBorderColor = (isFocused, isHover) => {
    if (isValid === false) return `${theme.palette.error.main} !important`; // Siempre error si es inválido
    if (isValid === true && showSuccessValidation)
      return `${theme.palette.success.main} !important`; // Verde si es válido y showSuccessValidation es true
    if (isFocused) return `${theme.palette.primary.main} !important`; // Color primario cuando está enfocado
    if (isValid === null) return `${theme.palette.secondary.main} !important`; // Verde si es válido y showSuccessValidation es true
    return `${theme.palette.blueGray200.main} !important`; // Color secundario por defecto
  };

  // Función auxiliar para obtener el color del label según el estado
  const getLabelColor = (isFocused, isHover) => {
    if (isValid === false) return `${theme.palette.error.main} !important`; // Error si es inválido o requerido y vacío
    if (isValid === true && showSuccessValidation)
      return `${theme.palette.success.main} !important`; // Verde si es válido y showSuccessValidation es true
    if (isFocused) return `${theme.palette.primary.main} !important`; // Color primario cuando está enfocado
    if (isHover) return `${theme.palette.secondary.dark} !important`; // Color primario cuando está enfocado
    if (isValid === null) return `${theme.palette.secondary.main} !important`; // Verde si es válido y showSuccessValidation es true
    return `${theme.palette.secondary.main} !important`; // Color secundario por defecto
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={adapterLocale}
      localeText={localeText}
    >
      <DatePicker
        label={`${label}${required ? " *" : ""}`}
        value={value}
        onChange={onChange}
        formatDensity="dense"
        className="input-date-picker"
        slotProps={{
          textField: {
            fullWidth: true,
            inputRef,
            onBlur,
            helperText,
            FormHelperTextProps: {
              sx: {
                fontSize: "11px",
                color:
                  isValid === false
                    ? `${theme.palette.error.main} !important`
                    : undefined,
              },
            },
            InputLabelProps: {
              sx: {
                color: getLabelColor(false, false),
                fontWeight: 600,
                "&.Mui-focused": {
                  backgroundColor: theme.palette.background.paper,
                  color: getLabelColor(true, false),
                },
                "&.Mui-error": {
                  color: getLabelColor(true, false),
                },
              },
            },
            InputProps: {
              sx: {
                "& input": {
                  textTransform: "uppercase",
                },
                "& .MuiIconButton-root": {
                  margin: "0 auto",
                },
              },
              ...rest,
            },
            sx: {
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: getBorderColor(false, false),
                },
                "&:hover fieldset": {
                  borderColor: getBorderColor(false, true),
                },
                "&.Mui-focused fieldset": {
                  borderColor: getBorderColor(true, false),
                },
              },
            },
          },
          popper: {
            sx: {
              "& .MuiPaper-root": {
                boxShadow: `0 0 4px 0 ${alpha("#646464", 0.5)}`,
                borderRadius: "5px",
                border: `1px solid ${theme.palette.blueGray200.main}`,
              },

              "& .MuiPickersCalendarHeader-root": {
                color: "#546E74",
                "& .MuiPickersCalendarHeader-labelContainer": {
                  fontSize: 14,
                },
              },
              "& .MuiDateCalendar-root": {},
              "& .MuiDayCalendar-root": {
                margin: "0 auto",
              },
              "& .MuiDayCalendar-weekContainer": {},
              "& .MuiDayCalendar-weekDayLabel": {
                color: "#546E74",
                fontSize: 12,
                marginX: "1px",
                width: 34,
                height: 34,
              },
              "& .MuiPickersDay-root": {
                color: "#546E74",
                fontSize: 12,
                marginX: "1px",
                width: 34,
                height: 34,
                "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.ethicsBlue.main,
                },
                "&.MuiPickersDay-today": {
                  borderColor: theme.palette.ethicsBlue.main,
                },
                "&:hover:not(.Mui-selected), &:focus:not(.Mui-selected)": {
                  backgroundColor: "lightgray",
                },
              },
            },
          },
        }}
        format={format}
        slots={{
          openPickerIcon: IconDatepicker,
          rightArrowIcon: RightArrowIcon,
          leftArrowIcon: LeftArrowIcon,
          switchViewIcon: SwitchViewIcon,
        }}
        maxDate={maxDate}
        minDate={minDate}
        {...rest}
      />
    </LocalizationProvider>
  );
};

export default EGDatePicker;
